import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { useState } from "react";
import { useSelector } from "react-redux";
import { genElObject, genElText } from "../../../../components/utils/EditorDialogElementGenerator";
import CustomerSelectorMain from "../../../selectors/customer-selector/component/customer-selector-main";

const nearExpiredOptions = [
  { label: "All", value: 0 },
  { label: "7 Days", value: 7 },
  { label: "14 Days", value: 14 },
  { label: "30 Days", value: 30 },
  { label: "60 Days", value: 60 },
  { label: "90 Days", value: 90 },
];

const usageStatusOptions = [
  // { label: "All", value: 0 },
  { label: "Not Used", value: 1 },
  { label: "Used", value: 2 },
];

const ReportCustomerVoucherFilter = (props) => {
  const { filterValue, setFilterValue, onClickRefresh } = props;
  const employee = useSelector((state) => state.auth.employee);
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);

  const handleOnConfirmCustomer = (event, datas) => {
    let selected;
    event.preventDefault();
    if (datas && datas.length > 0) {
      selected = datas[0];
      setFilterValue({ ...filterValue, data_customer: selected, customer_id: selected.id });
      setShowCustomerSelector(false);
    }
    return selected;
  };

  const elCustomer = genElObject({
    field: 'customer_id',
    name: 'Customer',
    value: filterValue.data_customer ? filterValue.data_customer.name : '',
    onClick: () => setShowCustomerSelector(true),
    onClear: () => setFilterValue({ ...filterValue, data_customer: null, customer_id: null }),
  });

  const elVoucher = genElText({
    field: 'name',
    name: 'Voucher Name',
    data: filterValue,
    setData: (newData) => setFilterValue({ ...filterValue, name: newData.name }),
    error: {},
    onEnter: () => { if (onClickRefresh) onClickRefresh(); },
  });

  const elVerifOwner = genElText({
    field: 'verif_owner_name',
    name: 'Approved By',
    data: filterValue,
    setData: (newData) => setFilterValue({ ...filterValue, verif_owner_name: newData.verif_owner_name }),
    error: {},
    onEnter: () => { if (onClickRefresh) onClickRefresh(); },
  });

  return (
    <div className="col-start-start w-full gap-2">
      <CustomerSelectorMain
        title="Select Customer"
        visible={showCustomerSelector}
        onHide={() => setShowCustomerSelector(false)}
        onConfirm={handleOnConfirmCustomer}
        additionalFilter={employee?.user_type == 3 ? `bc_id:=:${employee.id}` : ''}
      />

      {/* Top Row */}
      <div className="row-between-center w-full gap-4">
        <div className="row-start-center gap-4 w-full">
          <div className="col-start-start gap-1 w-full">{elCustomer}</div>
          <div className="col-start-start gap-1 w-full">{elVoucher}</div>
          {/* Expiring Soon */}
          <div className="col-start-start gap-1 w-full">
            <label className="font-bold text-sm text-slate-500">Expiring Soon</label>
            <Dropdown
              className="w-full"
              options={nearExpiredOptions}
              value={filterValue?.nearExpiredDays ?? 0}
              onChange={(e) => { setFilterValue({ ...filterValue, nearExpiredDays: e.value }); }}
              placeholder="All"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="row-end-center gap-2">
          <Button
            icon={`pi pi-chevron-${showAdvancedFilter ? "up" : "down"}`}
            className="p-button-text p-button-sm"
            onClick={() => setShowAdvancedFilter((v) => !v)}
            tooltip="Advanced Filter"
            tooltipOptions={{ position: "top" }}
          />
          <Button
            icon="pi pi-refresh"
            outlined
            rounded
            onClick={() => { if (onClickRefresh) onClickRefresh(filterValue); }}
          />
        </div>
      </div>

      {/* Advanced Filter */}
      <div
        className={`w-full overflow-hidden transition-all duration-300 ease-in-out ${showAdvancedFilter ? 'max-h-[350px] opacity-100' : 'max-h-0 opacity-0'
          }`}
      >
        <div className="row-start-center gap-4 w-full pt-2">
          {/* Usage Status */}
          <div className="col-start-start gap-1 w-full">
            <label className="font-bold text-sm text-slate-500">Usage Status</label>
            <Dropdown
              className="w-full"
              options={usageStatusOptions}
              value={filterValue?.usageStatus ?? 0}
              onChange={(e) => { setFilterValue({ ...filterValue, usageStatus: e.value }); }}
              placeholder="Semua"
            />
          </div>
          {/* Approved By */}
          <div className="col-start-start gap-1 w-full">{elVerifOwner}</div>
          {/* Created Date From */}
          <div className="col-start-start gap-1 w-full">
            <label className="font-bold text-sm text-slate-500">Created Date From</label>
            <Calendar
              className="w-full"
              value={filterValue?.dateFrom ? new Date(filterValue.dateFrom) : null}
              onChange={(e) => {
                setFilterValue({
                  ...filterValue,
                  dateFrom: e.value ?? null,
                });
              }}
              dateFormat="dd/mm/yy"
              showButtonBar
              readOnlyInput
              showIcon
            />
          </div>
          {/* Created Date To */}
          <div className="col-start-start gap-1 w-full">
            <label className="font-bold text-sm text-slate-500">Created Date To</label>
            <Calendar
              className="w-full"
              value={filterValue?.dateTo ? new Date(filterValue.dateTo) : null}
              onChange={(e) => {
                setFilterValue({
                  ...filterValue,
                  dateTo: e.value ?? null,
                });
              }}
              dateFormat="dd/mm/yy"
              showButtonBar
              readOnlyInput
              showIcon
            />
          </div>
          {/* Show Expired */}
          <div className="col-start-start gap-1 w-full">
            <label className="font-bold text-sm text-slate-500">Show Expired</label>
            <div className="row-start-center gap-2 h-[42px]">
              <Checkbox
                inputId="showExpiredVoucher"
                checked={filterValue?.showExpired ?? false}
                onChange={(e) => { setFilterValue({ ...filterValue, showExpired: e.checked }); }}
              />
              <label htmlFor="showExpiredVoucher" className="text-sm text-slate-600 cursor-pointer">Show expired items</label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportCustomerVoucherFilter;
